import { Mark, mergeAttributes } from '@tiptap/core';

// ═══════════════════════════════════════════════════════════════
// CommentMark (Fáze 3) — vazba rozsahu textu na komentář.
//
// Komentář je v editoru MARK (ne node), protože obaluje rozsah textu a text
// uvnitř zůstává normálně editovatelný. Mapuje se 1:1 na Word:
//   mark start/end  ↔  <w:commentRangeStart> / <w:commentRangeEnd>
//
// Mark nese JEN ID. Vlastní obsah komentáře (text, autor, datum, vlákno,
// vyřešení) žije v store (doc.comments) — do marku by se nevešel a musí
// přežít i to, že uživatel text smaže.
//
// V HTML serializaci: <span data-comment-id="3">…</span> — díky tomu vazba
// přežije getHTML() → export i round-trip.
// ═══════════════════════════════════════════════════════════════

export interface CommentMarkAttrs {
  commentId: number | null;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    commentMark: {
      /** Označí aktuální výběr jako komentovaný. */
      setCommentMark: (commentId: number) => ReturnType;
      /** Odstraní označení komentáře (např. při smazání komentáře). */
      unsetCommentMark: (commentId: number) => ReturnType;
    };
  }
}

export const CommentMark = Mark.create({
  name: 'commentMark',

  // Komentáře se můžou překrývat a vnořovat (Word to umí).
  excludes: '',
  inclusive: false,
  // Aby šel mark odstranit i když je uvnitř jiného formátování.
  keepOnSplit: false,

  addAttributes() {
    return {
      commentId: {
        default: null,
        parseHTML: (el) => {
          const v = el.getAttribute('data-comment-id');
          const n = v ? parseInt(v, 10) : NaN;
          return isNaN(n) ? null : n;
        },
        renderHTML: (attrs) =>
          attrs.commentId != null ? { 'data-comment-id': String(attrs.commentId) } : {},
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-comment-id]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes, { class: 'doc-comment-mark' }), 0];
  },

  addCommands() {
    return {
      setCommentMark:
        (commentId) =>
        ({ commands }) =>
          commands.setMark(this.name, { commentId }),

      unsetCommentMark:
        (commentId) =>
        ({ tr, state, dispatch }) => {
          // Projdi dokument a odstraň marky s daným ID (nemůžeme použít
          // prostý unsetMark — ten by sundal i marky jiných komentářů).
          const markType = state.schema.marks[this.name];
          if (!markType) return false;
          let found = false;
          state.doc.descendants((node, pos) => {
            if (!node.isText) return;
            node.marks.forEach((m) => {
              if (m.type === markType && m.attrs.commentId === commentId) {
                tr.removeMark(pos, pos + node.nodeSize, m);
                found = true;
              }
            });
          });
          if (found && dispatch) dispatch(tr);
          return found;
        },
    };
  },
});
